import React, { useState, useEffect, useRef } from 'react';
import './Experience.css';

const Experience = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const experience = [
    {
      id: 1,
      company: 'General Motors',
      positions: [
        {
          period: '2023 Dec – 2025 Jun',
          title: 'Tech Lead & Staff member — Computer Vision, Perception',
          area: 'Lane Detection & Parking',
          description: 'Built multi‑modal lane detection and autonomous parking models (camera + LiDAR). Designed prioritized training to focus on learnable, high‑value samples. Implemented SOTA architectures; improved accuracy and training efficiency.',
          tags: ['Camera+LiDAR', 'Prioritized Training', 'SOTA NN', 'Perception']
        },
        {
          period: '2023 Aug – Nov',
          title: 'Team Lead — Scene Understanding, Decision Making',
          area: 'Path Planning',
          description: 'Led 6 algorithm engineers. Delivered online nominal path generation via continuous optimization (Python prototypes).',
          tags: ['Leadership', 'Path Optimization', 'Python', 'Decision Making']
        },
        {
          period: '2021 – 2023 Aug',
          title: 'Applied Researcher — Reinforcement Learning, Decision Making',
          area: 'Planning (RL)',
          description: 'Applied Deep RL (Neural MCTS) to AV planning. Added Transformers for variable actors; cut training runtime by 50%. Designed shared Reward‑NMCTS training scheme.',
          tags: ['Neural MCTS', 'Transformers', 'RL', '-50% Runtime']
        }
      ]
    },
    {
      id: 2,
      company: 'LEVL Technologies',
      positions: [
        {
          period: '2019 – 2021',
          title: 'Senior Researcher',
          description: 'Signal processing and ML for RF device fingerprinting. Took models from research to product (statistical and deep learning).',
          tags: ['RF Fingerprinting', 'Signal Processing', 'Deep Learning', 'Productization']
        }
      ]
    },
    {
      id: 3,
      company: 'Unit 81',
      positions: [
        // {
        //   period: '2019 MAY – NOV',
        //   title: 'Senior Algorithm Engineer',
        //   description: 'Signal processing and communications with low‑power/area constraints.',
        //   tags: ['Signal Processing', 'Communications', 'Constraints']
        // },
        {
          period: '2017 – 2019',
          title: 'R&D Team Leader',
          description: 'Led 8 engineers. Designed and implemented FPGA‑based real‑time signal processing and communications.',
          tags: ['Leadership', 'FPGA', 'Real‑time', 'Signal Processing']
        },
        {
          period: '2013 – 2017',
          title: 'Signal Processing, Communication and Algorithm Engineer',
          description: 'Developed multi‑rate SP, adaptive filtering, closed‑loop tracking, inertial sensors/GPS processing, and FPGA logic for high‑frequency, real‑time systems. Earned an excellence certificate.',
          tags: ['Adaptive Filtering', 'Tracking', 'Inertial/GPS', 'FPGA']
        }
      ]
    }
  ];

  return (
    <section id="experience" className="experience" ref={sectionRef}>
      <div className="container">
        <div className={`experience-header ${isVisible ? 'visible' : ''}`}>
          <h2 className="section-title">Experience</h2>
          <div className="title-underline"></div>
          <p className="section-subtitle">Professional journey and key achievements</p>
        </div>

        <div className={`experience-content ${isVisible ? 'visible' : ''}`}>
          <div className="experience-timeline">
            {experience.map((company, companyIndex) => (
              <div key={company.id} className="company-block">
                <div className="company-header">
                  <h3 className="company-name">{company.company}</h3>
                </div>
                <div className="company-positions">
                  {company.positions.map((position, positionIndex) => (
                    <div key={positionIndex} className={`experience-item ${company.company.includes('General Motors') ? 'gm' : ''}`}>
                      <div className="experience-period">
                        <span className="period-text">{position.period}</span>
                      </div>
                      <div className="experience-details">
                        <div className="experience-header-row">
                          <h4 className="experience-title">{position.title}</h4>
                          {position.area && (
                            <span className={`position-area ${company.company.includes('General Motors') ? 'gm' : ''}`}>{position.area}</span>
                          )}
                        </div>
                        <p className="experience-description">{position.description}</p>
                        {position.tags && (
                          <div className="experience-tags">
                            {position.tags.map((tag, i) => (
                              <span className="exp-tag" key={i}>{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;